import { Home, List, Settings, DollarSign, Trello, Target, Database, Lightbulb, AlertCircle, RefreshCw, Bot, ClipboardList, Shield, BarChart3, Car, PieChart } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAuthStore } from '../../stores/authStore';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Acquisition Pipeline', href: '/pipeline', icon: Trello },
  { name: 'Programs', href: '/programs', icon: Car },
  { name: 'Parts', href: '/opportunities', icon: List },
  { name: 'S&OP Planning', href: '/planning', icon: Lightbulb },
  { name: 'Claims / Recovery', href: '/claims', icon: AlertCircle },
  { name: 'Approvals / Tasks', href: '/my-tasks', icon: ClipboardList },
  { name: 'Market Forecasts', href: '/volume-management', icon: RefreshCw },
  { name: 'Market Share', href: '/competitive-intelligence', icon: PieChart },
  { name: 'Import / Data Hub', href: '/import', icon: Database },
  { name: 'Goals', href: '/goals', icon: Target },
  { name: 'Flo AI', href: '/flo', icon: Bot, adminOnly: true },
  { name: 'Tariff Rates', href: '/tariff-rates', icon: DollarSign, adminOnly: true },
  { name: 'Reports', href: '/old-dashboard', icon: BarChart3, adminOnly: true },
  { name: 'Regression Tests', href: '/admin/regression', icon: Shield, adminOnly: true },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export default function Sidebar() {
  const location = useLocation();
  const { isCollapsed } = useSidebar();
  const user = useAuthStore((state) => state.user);
  const isAdmin = ['admin', 'owner', 'super_admin'].includes(user?.role);
  const visibleNavigation = navigation.filter((item) => !item.adminOnly || isAdmin);

  return (
    <aside className="hidden md:flex md:flex-shrink-0">
      <div 
        className={cn(
          "flex flex-col border-r bg-white dark:bg-gray-900 dark:border-gray-800 shadow-sm transition-all duration-300 ease-in-out",
          isCollapsed ? "w-16" : "w-64"
        )}
      >
        <nav className="flex-1 space-y-1 px-2 py-4">
          <TooltipProvider delayDuration={0}>
            {visibleNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              const isFloAI = item.href === '/flo';
              
              const linkContent = (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'group flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-gray-900 dark:bg-blue-600 text-white shadow-sm'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white',
                    isCollapsed && 'justify-center'
                  )}
                >
                  <item.icon
                    className={cn(
                      'h-5 w-5 flex-shrink-0 transition-colors',
                      isActive 
                        ? 'text-white' 
                        : isFloAI 
                          ? 'text-[#07689f]' 
                          : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200',
                      !isCollapsed && 'mr-3'
                    )}
                  />
                  {!isCollapsed && (
                    <span className="truncate">{item.name}</span>
                  )}
                </Link>
              );

              // Wrap with tooltip only when collapsed
              if (isCollapsed) {
                return (
                  <Tooltip key={item.name}>
                    <TooltipTrigger asChild>
                      {linkContent}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {item.name}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return linkContent;
            })}
          </TooltipProvider>
        </nav>
      </div>
    </aside>
  );
}
